import { toPng } from 'html-to-image';

type RenderLabelToPngArgs = {
  html: string;
  widthPx: number;
  heightPx: number;
  pixelRatio?: number;
  debugDownload?: boolean;
  autoFit?: boolean;
};

const DATA_URL_PREFIX = 'data:image/png;base64,';
const BASE64_RE = /^[A-Za-z0-9+/=]+$/;

/** Shared by the live preview, raster output and browser fallback document. */
export function fitLabelContent(target: HTMLElement, size?: { widthPx: number; heightPx: number }) {
  const fitNode = (target.querySelector('[data-label-content], .ticket-container') as HTMLElement) ||
    (target.firstElementChild as HTMLElement) || target;
  const widthPx = size?.widthPx || target.clientWidth || target.getBoundingClientRect().width;
  const heightPx = size?.heightPx || target.clientHeight || target.getBoundingClientRect().height;
  if (!widthPx || !heightPx) return;
  const marginPx = fitNode.hasAttribute('data-label-content') ? 0 : 4;
  const innerWidth = Math.max(0, widthPx - marginPx * 2);
  const innerHeight = Math.max(0, heightPx - marginPx * 2);
  target.style.boxSizing = 'border-box';
  target.style.padding = `${marginPx}px`;
  fitNode.style.boxSizing = 'border-box';
  fitNode.style.transform = '';
  fitNode.style.transformOrigin = 'top left';
  fitNode.style.width = `${innerWidth}px`;
  fitNode.style.maxWidth = 'none';
  fitNode.style.marginLeft = '0';
  fitNode.style.marginRight = '0';
  const contentWidth = fitNode.scrollWidth || fitNode.getBoundingClientRect().width;
  const contentHeight = fitNode.scrollHeight || fitNode.getBoundingClientRect().height;
  const scale = Math.min(1, innerWidth / contentWidth, innerHeight / contentHeight);
  if (scale > 0 && scale < 1) fitNode.style.transform = `scale(${scale})`;
}

export function stripDataUrlPrefix(dataUrlOrBase64: string): string {
  if (dataUrlOrBase64.startsWith(DATA_URL_PREFIX)) {
    return dataUrlOrBase64.slice(DATA_URL_PREFIX.length);
  }
  const commaIndex = dataUrlOrBase64.indexOf(',');
  if (commaIndex !== -1) {
    return dataUrlOrBase64.slice(commaIndex + 1);
  }
  return dataUrlOrBase64;
}

export function normalizeBase64Image(input: string): string {
  if (!input) throw new Error('Empty base64 image');
  let b64 = input.trim();
  if (b64.startsWith('data:')) {
    const commaIndex = b64.indexOf(',');
    if (commaIndex !== -1) {
      b64 = b64.slice(commaIndex + 1);
    }
  }
  b64 = b64.replace(/\s+/g, '');
  if (b64.length < 50) throw new Error('Base64 too short to be a valid image');
  if (!BASE64_RE.test(b64)) throw new Error('Base64 contains invalid characters');
  return b64;
}

export function guessImageMimeFromBase64(b64: string): 'png' | 'jpg' | 'unknown' {
  if (b64.startsWith('iVBORw0')) return 'png';
  if (b64.startsWith('/9j/')) return 'jpg';
  return 'unknown';
}

type LabelLayoutArgs = Pick<RenderLabelToPngArgs, 'html' | 'widthPx' | 'heightPx' | 'autoFit'>;

async function withLabelRenderTarget<T>(
  { html, widthPx, heightPx, autoFit = true }: LabelLayoutArgs,
  render: (target: HTMLElement, container: HTMLElement) => Promise<T> | T
): Promise<T> {
  const temp = document.createElement('div');
  temp.style.position = 'absolute';
  temp.style.left = '-10000px';
  temp.style.top = '0';
  temp.style.background = 'white';
  temp.style.width = `${widthPx}px`;
  temp.style.height = `${heightPx}px`;
  temp.style.overflow = 'hidden';
  temp.style.display = 'block';
  temp.style.boxSizing = 'border-box';
  temp.style.zIndex = '-1';
  temp.style.pointerEvents = 'none';

  temp.innerHTML = html;
  document.body.appendChild(temp);

  try {
    const imgs = Array.from(temp.querySelectorAll('img'));
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise((res) => {
            if (img.complete) {
              res(true);
              return;
            }
            img.onload = () => res(true);
            img.onerror = () => res(true);
          })
      )
    );

    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {
        // Ignore font readiness errors and continue.
      }
    }

    await new Promise((res) => window.requestAnimationFrame(() => window.requestAnimationFrame(res)));

    const target = (temp.firstElementChild as HTMLElement) || temp;
    if (autoFit) {
      fitLabelContent(target, { widthPx, heightPx });
    }
    return await render(target, temp);
  } finally {
    document.body.removeChild(temp);
  }
}

/** Fit once before QZ HTML transport, just as preview and raster output do. */
export async function fitLabelHtmlForPrint(args: LabelLayoutArgs): Promise<string> {
  return withLabelRenderTarget(args, (_target, container) => container.innerHTML);
}

export async function renderHtmlToPngBase64({
  html, widthPx, heightPx, pixelRatio = 3, debugDownload = false, autoFit = true
}: RenderLabelToPngArgs): Promise<string> {
  return withLabelRenderTarget({ html, widthPx, heightPx, autoFit }, async (target) => {
    const dataUrl = await toPng(target, {
      pixelRatio,
      width: widthPx,
      height: heightPx,
      backgroundColor: '#fff',
      // Avoid cross-origin stylesheet access errors when embedding webfonts.
      skipFonts: true,
      style: {
        width: `${widthPx}px`,
        height: `${heightPx}px`
      }
    });
    if (debugDownload) {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'label-debug.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    return normalizeBase64Image(stripDataUrlPrefix(dataUrl));
  });
}
